import React from "react";

import { t } from "i18next";
import { useTranslation } from "react-i18next";
import { custom } from "zod";

import { ButtonProps } from "Atoms/Buttons/Button";

import { OnlinePayments } from "Pages/OnlinePayments";

import {
  Button,
  Price,
  PrimaryAction,
  useSubmitButton,
  LinkButton,
} from "../Atoms";
import { useBillSplit, useCurrentBillSplit } from "../BillSplit/queries";
import { TabDetails } from "../Cart/CartDetailsForm";
import { CartItems } from "../Cart/CartItems";
import { OrderedOrderItems } from "../Cart/OrderedOrderItem";
import { createTabCheckoutSession } from "../Cart/requests";
import { useCustomer } from "../Customer/queries";
import {
  useCurrentCustomerTab,
  useCustomerTab,
  useLoadedCustomerTab,
} from "../CustomerTab/queries";
import { CustomerTab } from "../CustomerTab/schema";
import { useSession } from "../Session/SessionContext";
import { getTabTotal, canOrder } from "../Session/sessionSelectors";
import { useVenue } from "../VenueContext";
import {
  getVenueHasTablePay,
  getVenueHasTablePayOrdering,
} from "../venueSelectors";

export const useStartTabCheckoutSession = () => {
  const [submissionState, trackSubmit] = useSubmitButton();
  const session = useSession();
  const venue = useVenue();
  const hasTablePay = getVenueHasTablePay(venue);

  const startTabCheckoutSession = async () => {
    const customerTab = session.customerTab;
    if (!customerTab?.uuid) {
      throw "Can't make payment on unsaved customer tab";
    }
    if (!hasTablePay) {
      console.error("Ordering is not enabled");
      return;
    }
    const checkoutUrl = await createTabCheckoutSession(customerTab.uuid);
    window.location.assign(checkoutUrl);
    await new Promise((r) => setTimeout(r, 3000));
  };

  return {
    startTabCheckoutSession: trackSubmit(startTabCheckoutSession),
    submissionState,
  };
};

export const StartTabCheckoutSession = ({}: {}) => {
  const { t } = useTranslation();
  const session = useSession();
  const { startTabCheckoutSession, submissionState } =
    useStartTabCheckoutSession();

  return (
    <Button
      type="button"
      onClick={startTabCheckoutSession}
      submissionState={submissionState}
      primary
    >
      {t("cart.payNow")}{" "}
      <Price
        showCurrency
        showDecimals
        value={getTabTotal(session.customerTab)}
      />
    </Button>
  );
};
const LinkToSplitPayments = () => {
  const { t } = useTranslation();
  const { data: customerTab } = useCurrentCustomerTab();
  const { data: billSplitSession } = useCurrentBillSplit();
  const { data: customer } = useCustomer();
  if (!customerTab) {
    return null;
  }

  if (
    billSplitSession?.customerSessions.find(
      (cs) => cs.customerId === customer?.uuid
    )
  ) {
    return (
      <LinkButton primary to="/billSplit">
        {t("billSplit.continueSplitting")}{" "}
        <Price value={customerTab.balance} showCurrency />
      </LinkButton>
    );
  }
  if (!billSplitSession) {
    return (
      <LinkButton primary to="/billSplit/start">
        {t("billSplit.settleUp")}{" "}
        <Price value={customerTab.balance} showCurrency />
      </LinkButton>
    );
  } else {
    return (
      <LinkButton primary to="/billSplit/join">
        {t("billSplit.settleUp")}{" "}
        <Price value={customerTab.balance} showCurrency />
      </LinkButton>
    );
  }
};
export const TablePayPayment = ({
  customerTab,
}: {
  customerTab: CustomerTab;
}) => {
  const { t } = useTranslation();
  const venue = useVenue();
  const hasTablePay = getVenueHasTablePay(venue);
  const hasTablePayOrdering = getVenueHasTablePayOrdering(venue);
  const hasBillSplitting = venue.enabledFeatures.billSplit;

  const totalPaid = customerTab.transactions
    .map((t) => t.taxInclusive)
    .reduce((a, b) => a + b, 0);

  return (
    <div>
      <div className="space-y-32 md:space-y-32 md:border-[6px] border-venue-tertiary rounded-venue-brand md:p-16 md:max-w-screen-sm md:mx-auto">
        <div className="flex-1 space-y-32">
          {hasTablePayOrdering && <CartItems />}
          {hasTablePayOrdering && (
            <div className="">
              <LinkButton secondary to="/">
                {t("cart.addMoreItems")}
              </LinkButton>
            </div>
          )}
          <TabDetails customerTab={customerTab} />
          {hasTablePay && (
            <div className="hidden md:block flex-1">
              {hasBillSplitting ? (
                <LinkToSplitPayments />
              ) : (
                <StartTabCheckoutSession />
              )}
            </div>
          )}
          {totalPaid != 0 && (
            <>
              <div className="font-bold text-lg flex justify-between">
                <div>{t("onlinePayments.paymentReceived")}</div>
                <div>
                  <Price value={totalPaid} showCurrency />
                </div>
              </div>
              <div className="font-bold text-lg flex justify-between">
                <div>{t("onlinePayments.totalLeftToPay")}</div>
                <div>
                  <Price value={customerTab.balance} showCurrency />
                </div>
              </div>
            </>
          )}

          <OrderedOrderItems />
        </div>
      </div>
      {hasTablePay && (
        <PrimaryAction>
          {hasBillSplitting ? (
            <LinkToSplitPayments />
          ) : (
            <StartTabCheckoutSession />
          )}
        </PrimaryAction>
      )}
    </div>
  );
};
